import { LandingPageContent } from '../types';

/**
 * Seasonal page for Eid gifting. Published far ahead of Ramadan (≈ Feb 2027)
 * deliberately — like the Christmas page, seasonal rankings are earned months
 * in advance, and the page also catches Eid al-Adha and year-round "eidi
 * ideas" queries.
 */
const eidGiftsForKids: LandingPageContent = {
	path: '/gifts/eid-gifts-for-kids',
	seoTitle: 'Eid Gifts for Kids — Personalized Ideas Beyond the Eidiyah',
	metaDescription:
		'Eid gift ideas for kids: personalized storybooks starring your child — available in Arabic with proper right-to-left layout — coloring books and keepsakes that outlast the sugar rush.',
	heading: 'Eid Gifts for Kids',
	subheading: 'Something to open after the eidiyah is counted.',
	heroImage: '/assets/hero/kid-4/classic_scene.png',
	breadcrumbParents: [{ name: 'Gifts for kids', url: '/gifts-for-kids' }],
	intro: `Eid morning has a well-worn script: new clothes, prayers, breakfast at somebody's grandmother's table, and the ceremonial handing over of eidiyah — crisp notes the children count immediately and spend on nothing memorable.

The cash is tradition and nobody is arguing with it. But more families each year add one *wrapped* thing: a gift that marks the day itself. That is where a personalized book earns its place — a story where the child is the illustrated hero, opened on a morning that is already about celebration and family.

And unlike most of what gets bought in the Eid rush, it is still being read at the next Eid.`,
	sections: [
		{
			heading: 'Why a book, on this of all mornings',
			body: `Eid gifts compete with sugar, cousins and new clothes — strong opposition. A generic toy loses that fight by mid-morning.

A book with the child's own face on the cover doesn't. It gets shown to every arriving relative — which, on Eid, is a considerable audience — and each showing re-attaches the child to the gift. By evening, when the house quiets down, it becomes the bedtime story. There are very few gifts that work both ends of an Eid day.

For the giver, it also solves the eidiyah asymmetry: cash from an aunt is forgotten by Friday, but the aunt who gave the book in which Omar rescues a falcon is remembered by name.`,
		},
		{
			heading: 'In Arabic, if you want it',
			body: `A personalized Eid gift can carry the language along with the occasion. Stories can be produced in Arabic with a properly mirrored right-to-left layout — spine on the right, pages turning the correct way — and the child's name rendered in Arabic script.

For diaspora families, this is the quiet second gift inside the first: Eid arrives, and with it a book that keeps Arabic attached to celebration rather than schoolwork. More on our [personalized Arabic books page](/personalized-arabic-books-for-kids).`,
		},
		{
			heading: 'Timing around Ramadan and both Eids',
			body: `Personalized gifts are made to order — artwork, your approval, printing, shipping. For **Eid al-Fitr**, the practical deadline is mid-Ramadan; ordering as Ramadan begins is comfortable, and has the side benefit that the book can arrive in time for the last nights of the month, when children's patience for waiting is at its thinnest.

**Eid al-Adha** gives more warning and the same rules apply: two to three weeks ahead is relaxed, less than that is courier roulette.

Ramadan itself is worth considering as the occasion: a new bedtime book at the start of the month, in Arabic or English, pairs naturally with the month's rhythm of early nights and family evenings.`,
		},
		{
			heading: 'Ideas by age',
			body: `**Ages 2–5.** A personalized storybook is the centrepiece — short, bright, and about them. Add a personalized coloring book for the long family afternoons of Eid.

**Ages 6–8.** Early readers take real pride in reading *their own* story aloud to relatives — an Eid party trick with unusually good production values.

**Ages 9+.** Keepsakes over storybooks: a custom t-shirt or mug of their illustrated character, or a poster for the bedroom wall. Pairs well with the eidiyah rather than replacing it — nobody should attempt to replace the eidiyah.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'The centrepiece Eid gift — available in Arabic or English.',
		},
		{
			label: 'Personalized Arabic books',
			href: '/personalized-arabic-books-for-kids',
			description: 'Proper right-to-left editions with the name in Arabic script.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'For the family afternoons between meals.',
		},
	],
	faqs: [
		{
			question: 'What is a good Eid gift for a child?',
			answer:
				'Alongside the traditional eidiyah, a personalized storybook — where the child appears as the illustrated hero — works particularly well: it gets shown to every visiting relative on Eid day and becomes the bedtime story that night. For older children, custom keepsakes such as t-shirts or posters of their illustrated character land better.',
		},
		{
			question: 'When should I order an Eid gift?',
			answer:
				'Personalized gifts are made to order, so allow two to three weeks. For Eid al-Fitr, ordering by mid-Ramadan is the practical deadline; ordering as Ramadan begins is comfortable.',
		},
		{
			question: 'Can the Eid gift be in Arabic?',
			answer:
				'Yes. Stories can be produced in Arabic with a properly mirrored right-to-left layout and the child’s name in Arabic script — a gift that carries the language along with the occasion.',
		},
		{
			question: 'Is a book an appropriate Eid gift alongside eidiyah?',
			answer:
				'Yes — it complements rather than replaces the cash tradition. The eidiyah is spent and forgotten; the book with the child’s face on the cover is still on the shelf at the next Eid, attached to the person who gave it.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Personalized Arabic books for kids', href: '/personalized-arabic-books-for-kids' },
		{ label: 'Personalized gifts for kids', href: '/personalized-gifts-for-kids' },
	],
	cta: { label: 'Browse Eid gifts', href: '/products' },
};

export default eidGiftsForKids;
